import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import TimeEntryService from '../../services/time-entry.service';
import { NumberTextField } from '../../components/common';
import EmployeeService from '../../services/employee.service';
import ProjectService from '../../services/project.service';
import TaskService from '../../services/task.service';

const TimeEntryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    employee: '',
    date: new Date().toISOString().split('T')[0],
    hours: 0,
    billable: true,
  });

  useEffect(() => {
    fetchEmployees();
    fetchProjects();
    if (isEdit && id) {
      fetchTimeEntry(parseInt(id));
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (formData.project) {
      fetchTasksByProject(formData.project);
    } else {
      setTasks([]);
    }
  }, [formData.project]);

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAll();
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await ProjectService.getAll({ populate: '*' });
      setProjects(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTasksByProject = async (projectId: number) => {
    try {
      const response = await TaskService.getByProject(projectId, { populate: '*' });
      setTasks(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchTimeEntry = async (entryId: number) => {
    try {
      const response = await TimeEntryService.getOne(entryId);
      const entry = response.data?.data?.attributes;
      if (entry) {
        setFormData({
          ...entry,
          employee: entry.employee?.data?.id,
          project: entry.project?.data?.id,
          task: entry.task?.data?.id,
        });
      }
    } catch (error) {
      console.error('Error fetching time entry:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    const newValue = e.target.type === 'checkbox' ? checked : value;
    
    setFormData((prev: any) => {
      const updated = { ...prev, [name]: newValue };
      
      // Calculate total cost if hours and hourly rate are present
      if ((name === 'hours' || name === 'hourlyRate') && updated.hours && updated.hourlyRate) {
        updated.totalCost = parseFloat(updated.hours as any) * parseFloat(updated.hourlyRate as any);
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await TimeEntryService.update(parseInt(id), formData);
      } else {
        await TimeEntryService.create(formData);
      }
      navigate('/time-entries');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save time entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Time Entry' : 'Create Time Entry'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                required
                label="Employee"
                name="employee"
                value={formData.employee || ''}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    employee: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              >
                <MenuItem value="">Select Employee</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.attributes?.firstName} {emp.attributes?.lastName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                required
                label="Date"
                name="date"
                type="date"
                value={formData.date || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                label="Project"
                name="project"
                value={formData.project || ''}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    project: e.target.value ? parseInt(e.target.value) : undefined,
                    task: undefined, // Reset task when project changes
                  }))
                }
              >
                <MenuItem value="">No Project</MenuItem>
                {projects.map((proj) => (
                  <MenuItem key={proj.id} value={proj.id}>
                    {proj.attributes?.projectName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                label="Task"
                name="task"
                value={formData.task || ''}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    task: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
                disabled={!formData.project}
              >
                <MenuItem value="">No Task</MenuItem>
                {tasks.map((task) => (
                  <MenuItem key={task.id} value={task.id}>
                    {task.attributes?.title}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Start Time"
                name="startTime"
                type="time"
                value={formData.startTime || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Time"
                name="endTime"
                type="time"
                value={formData.endTime || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                required
                label="Hours"
                name="hours"
                value={formData.hours || 0}
                onChange={handleChange}
                inputProps={{ step: '0.25', min: '0' }}
              />
              <NumberTextField
                fullWidth
                label="Hourly Rate"
                name="hourlyRate"
                value={formData.hourlyRate || ''}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Stack>

            <NumberTextField
              fullWidth
              label="Total Cost"
              name="totalCost"
              value={formData.totalCost || ''}
              onChange={handleChange}
              inputProps={{ step: '0.01', min: '0' }}
              helperText="Automatically calculated from hours × hourly rate"
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.billable || false}
                  onChange={handleChange}
                  name="billable"
                />
              }
              label="Billable"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/time-entries')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default TimeEntryForm;

